import {
  ABSENCE_TYPE_LABELS,
  ABSENCE_TYPES,
  type AbsenceChart,
  type AbsenceItem,
  type AbsenceType,
  createVacationSchema,
  VACATION_STATUS_LABELS,
  VACATION_TYPE_LABELS,
  VACATION_TYPES,
  type VacationListItem,
  type VacationType,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { type CSSProperties, type FormEvent, Fragment, useMemo, useState } from 'react';
import { Dialog } from '@/components/dialog';
import { DateRange } from '@/components/list-tools';
import { EntityStatus, ProcessHint } from '@/components/process';
import {
  Avatar,
  Badge,
  Button,
  ErrorBox,
  Field,
  Input,
  PageHeader,
  Segmented,
  Select,
  Spinner,
  Textarea,
} from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/lib/auth';
import { formatMoment, formatRange, plural, todayIso } from '@/lib/format';
import {
  absenceChartQuery,
  assigneesQuery,
  departmentsTreeQuery,
  flattenTree,
  vacationsQuery,
} from '@/lib/queries';

export interface VacationsSearch {
  tab?: 'mine' | 'all';
  month?: string;
  department?: string;
  /** Тип отсутствия: в графике остаются только такие записи и люди с ними */
  type?: AbsenceType;
  /** Масштаб графика: месяц (по умолчанию), неделя или один день */
  scale?: 'week' | 'day';
  /** День, от которого строится неделя или день (ГГГГ-ММ-ДД) */
  day?: string;
  /** Список заявлений: сотрудник, период, вид отпуска */
  person?: string;
  from?: string;
  to?: string;
  kind?: VacationType;
}

const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];
const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

export const ABSENCE_COLORS: Record<AbsenceType, string> = {
  vacation: '#2b7a4b',
  business_trip: '#1f5d99',
  sick: '#b8322a',
  dayoff: '#7a5b00',
  other: '#5e6e7a',
};

/** Даты `YYYY-MM-DD` сравниваются как строки */
const later = (a: string, b: string) => (a > b ? a : b);
const earlier = (a: string, b: string) => (a < b ? a : b);

function monthBounds(month: string) {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return { y, m, days: last, from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-${pad(last)}` };
}

const MONTHS_GEN = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];
const WEEKDAYS_FULL = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];

function addDaysIso(iso: string, n: number) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function mondayOf(iso: string) {
  const wd = new Date(`${iso}T00:00:00Z`).getUTCDay() || 7;
  return addDaysIso(iso, 1 - wd);
}

const dayNum = (iso: string) => Number(iso.slice(8, 10));
const monthIdx = (iso: string) => Number(iso.slice(5, 7)) - 1;

/** Период графика по масштабу: все дни подряд, заголовок, сдвиг «назад / вперёд». */
function chartRange(scale: 'month' | 'week' | 'day', month: string, day: string) {
  if (scale === 'month') {
    const b = monthBounds(month);
    const days = Array.from({ length: b.days }, (_, i) => addDaysIso(b.from, i));
    return { from: b.from, to: b.to, days, title: `${MONTHS[b.m - 1]} ${b.y}` };
  }
  if (scale === 'week') {
    const from = mondayOf(day);
    const to = addDaysIso(from, 6);
    const days = Array.from({ length: 7 }, (_, i) => addDaysIso(from, i));
    const sameMonth = monthIdx(from) === monthIdx(to);
    const title = sameMonth
      ? `${dayNum(from)}–${dayNum(to)} ${MONTHS_GEN[monthIdx(to)]} ${to.slice(0, 4)}`
      : `${dayNum(from)} ${MONTHS_GEN[monthIdx(from)]} – ${dayNum(to)} ${MONTHS_GEN[monthIdx(to)]} ${to.slice(0, 4)}`;
    return { from, to, days, title };
  }
  const wd = new Date(`${day}T00:00:00Z`).getUTCDay();
  return {
    from: day,
    to: day,
    days: [day],
    title: `${WEEKDAYS_FULL[wd]}, ${dayNum(day)} ${MONTHS_GEN[monthIdx(day)]} ${day.slice(0, 4)}`,
  };
}

function shiftMonth(month: string, delta: number) {
  const { y, m } = monthBounds(month);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function VacationsPage() {
  const search = useSearch({ strict: false }) as VacationsSearch;
  const navigate = useNavigate();
  const can = useCan();
  const [creating, setCreating] = useState(false);
  const tab = search.tab ?? 'chart';

  const setSearch = (patch: Partial<VacationsSearch>) =>
    navigate({ to: '/vacations', search: { ...search, ...patch }, replace: true });

  const tabs: { value: 'chart' | 'mine' | 'all'; label: string }[] = [
    { value: 'chart', label: 'График отсутствий' },
    { value: 'mine', label: 'Мои заявления' },
  ];
  if (can('vacations.request.read_all')) tabs.push({ value: 'all', label: 'Все заявления' });

  return (
    <div>
      <PageHeader
        title="Отпуска"
        description="Заявление проходит согласование у руководителей до директора, затем кадровик оформляет отпуск."
        actions={
          can('vacations.request.create') && (
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden />
              Подать заявление
            </Button>
          )
        }
      />
      {creating && <VacationFormDialog onClose={() => setCreating(false)} />}
      <div className="mb-4">
        <Segmented
          label="Раздел"
          value={tab}
          options={tabs}
          onChange={(v) => setSearch({ tab: v === 'chart' ? undefined : v })}
        />
      </div>
      {tab === 'chart' ? (
        <ChartView search={search} setSearch={setSearch} />
      ) : (
        <VacationList scope={tab} search={search} setSearch={setSearch} onCreate={() => setCreating(true)} />
      )}
    </div>
  );
}

// ---------- График отсутствий ----------

function ChartView({
  search,
  setSearch,
}: {
  search: VacationsSearch;
  setSearch: (p: Partial<VacationsSearch>) => void;
}) {
  const today = todayIso();
  const scale = search.scale ?? 'month';
  const month = search.month ?? today.slice(0, 7);
  const day = search.day ?? today;
  const range = chartRange(scale, month, day);
  const tree = useQuery(departmentsTreeQuery);
  const chart = useQuery(
    absenceChartQuery({ from: range.from, to: range.to, departmentId: search.department }),
  );
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState<{ item: AbsenceItem; name: string } | null>(null);
  const shown = useMemo(() => {
    if (!chart.data || !search.type) return chart.data;
    return {
      ...chart.data,
      employees: chart.data.employees
        .map((e) => ({ ...e, absences: e.absences.filter((a) => a.type === search.type) }))
        .filter((e) => e.absences.length > 0),
    };
  }, [chart.data, search.type]);

  const shift = (delta: number) =>
    scale === 'month'
      ? setSearch({ month: shiftMonth(month, delta) })
      : setSearch({ day: addDaysIso(day, delta * (scale === 'week' ? 7 : 1)) });
  const isCurrent =
    scale === 'month'
      ? month === today.slice(0, 7)
      : scale === 'week'
        ? mondayOf(day) === mondayOf(today)
        : day === today;
  const unit = scale === 'month' ? 'месяц' : scale === 'week' ? 'неделя' : 'день';

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Segmented
          label="Масштаб"
          value={scale}
          options={[
            { value: 'month', label: 'Месяц' },
            { value: 'week', label: 'Неделя' },
            { value: 'day', label: 'День' },
          ]}
          onChange={(v) =>
            setSearch(
              v === 'month'
                ? // Из недели или дня — в месяц того же дня
                  {
                    scale: undefined,
                    day: undefined,
                    month: day.slice(0, 7) === today.slice(0, 7) ? undefined : day.slice(0, 7),
                  }
                : {
                    scale: v,
                    day: scale === 'month' && month !== today.slice(0, 7) ? `${month}-01` : search.day,
                  },
            )
          }
        />
        <div className="flex items-center gap-1">
          <Button
            variant="secondary"
            size="sm"
            aria-label={`Предыдущ${unit === 'неделя' ? 'ая' : 'ий'} ${unit}`}
            onClick={() => shift(-1)}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <h2 className="min-w-[150px] px-1 text-center text-[16px] font-semibold first-letter:uppercase">
            {range.title}
          </h2>
          <Button
            variant="secondary"
            size="sm"
            aria-label={`Следующ${unit === 'неделя' ? 'ая' : 'ий'} ${unit}`}
            onClick={() => shift(1)}
          >
            <ChevronRight className="size-4" />
          </Button>
          {!isCurrent && (
            <Button variant="ghost" size="sm" onClick={() => setSearch({ month: undefined, day: undefined })}>
              Сегодня
            </Button>
          )}
        </div>
        <Select
          aria-label="Отдел"
          value={search.department ?? ''}
          onChange={(e) => setSearch({ department: e.target.value || undefined })}
          className="w-full sm:w-[240px]"
        >
          <option value="">Все отделы</option>
          {flattenTree(tree.data ?? []).map(({ node, depth }) => (
            <option key={node.id} value={node.id}>
              {'  '.repeat(depth)}
              {node.name}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Тип отсутствия"
          value={search.type ?? ''}
          onChange={(e) => setSearch({ type: (e.target.value || undefined) as AbsenceType | undefined })}
          className="w-full sm:w-[200px]"
        >
          <option value="">Все типы</option>
          {ABSENCE_TYPES.map((t) => (
            <option key={t} value={t}>
              {ABSENCE_TYPE_LABELS[t]}
            </option>
          ))}
        </Select>
        {chart.data?.canManage && (
          <Button variant="secondary" size="sm" className="sm:ml-auto" onClick={() => setAdding(true)}>
            <Plus className="size-4" aria-hidden />
            Внести отсутствие
          </Button>
        )}
      </div>
      <Legend />
      {chart.isPending ? (
        <Spinner />
      ) : chart.error ? (
        <ErrorBox error={chart.error} />
      ) : scale === 'day' ? (
        <DayList
          chart={shown ?? chart.data}
          day={day}
          onSelect={(item, name) => setSelected({ item, name })}
        />
      ) : shown && shown.employees.length === 0 && search.type ? (
        <p className="panel px-4 py-8 text-center text-steel">
          {scale === 'week' ? 'На этой неделе' : 'В этом месяце'} нет записей «
          {ABSENCE_TYPE_LABELS[search.type]}».
        </p>
      ) : (
        <ChartGrid
          chart={shown ?? chart.data}
          range={range}
          wide={scale === 'week'}
          today={today}
          onSelect={(item, name) => setSelected({ item, name })}
        />
      )}
      {adding && chart.data && <AbsenceDialog chart={chart.data} onClose={() => setAdding(false)} />}
      {selected && (
        <AbsenceDetailsDialog
          item={selected.item}
          name={selected.name}
          canManage={Boolean(chart.data?.canManage)}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function Legend() {
  return (
    <ul className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-[12.5px] text-ink-soft">
      {ABSENCE_TYPES.map((t) => (
        <li key={t} className="flex items-center gap-1.5">
          <span aria-hidden className="size-2.5 rounded-sm" style={{ background: ABSENCE_COLORS[t] }} />
          {ABSENCE_TYPE_LABELS[t]}
        </li>
      ))}
      <li className="flex items-center gap-1.5">
        <span
          aria-hidden
          className="h-2.5 w-4 rounded-sm border border-dashed border-[#2b7a4b] bg-[#2b7a4b]/15"
        />
        На согласовании
      </li>
    </ul>
  );
}

function ChartGrid({
  chart,
  range,
  wide,
  today,
  onSelect,
}: {
  chart: AbsenceChart;
  range: { from: string; to: string; days: string[] };
  /** Неделя: широкие колонки, в полосе видно комментарий */
  wide?: boolean;
  today: string;
  onSelect: (item: AbsenceItem, name: string) => void;
}) {
  const bounds = range;
  const days = useMemo(
    () =>
      range.days.map((iso) => {
        const wd = new Date(`${iso}T00:00:00Z`).getUTCDay();
        return { n: dayNum(iso), iso, wd, weekend: wd === 0 || wd === 6 };
      }),
    [range.days],
  );
  const groups = useMemo(() => {
    const out: { name: string; people: AbsenceChart['employees'] }[] = [];
    for (const e of chart.employees) {
      const name = e.departmentName ?? 'Без отдела';
      const last = out.at(-1);
      if (last && last.name === name) last.people.push(e);
      else out.push({ name, people: [e] });
    }
    return out;
  }, [chart.employees]);

  if (!chart.employees.length)
    return <div className="panel px-6 py-10 text-center text-steel">Сотрудников нет.</div>;

  const gridStyle: CSSProperties = {
    gridTemplateColumns: `minmax(150px, 220px) repeat(${days.length}, minmax(${wide ? 80 : 24}px, 1fr))`,
  };
  /** Колонка дня в сетке (1-я — сотрудник); даты за пределами периода прижимаются к краю */
  const col = (iso: string) => {
    const clamped = iso < range.from ? range.from : iso > range.to ? range.to : iso;
    return days.findIndex((d) => d.iso === clamped) + 2;
  };

  return (
    <div className="panel relative overflow-x-auto">
      <section className={wide ? 'min-w-[740px]' : 'min-w-[900px]'} aria-label="График отсутствий">
        <div
          className="sticky top-0 z-20 grid border-b border-line bg-surface text-[11.5px]"
          style={gridStyle}
        >
          <div className="sticky left-0 z-10 bg-surface px-3 py-1.5 text-steel">Сотрудник</div>
          {days.map((d) => (
            <div
              key={d.iso}
              className={clsx(
                'flex flex-col items-center py-1 tabular-nums',
                d.weekend ? 'bg-sunken/70 text-steel' : 'text-ink-soft',
                d.iso === today && 'shadow-[inset_0_2px_0_var(--color-signal)] font-semibold text-ink',
              )}
            >
              {wide ? (
                <span className="py-0.5 text-[12px]">
                  {WEEKDAYS[d.wd]}, {d.n} {MONTHS_GEN[monthIdx(d.iso)]}
                </span>
              ) : (
                <>
                  <span>{d.n}</span>
                  <span className="text-[10px] opacity-75">{WEEKDAYS[d.wd]}</span>
                </>
              )}
            </div>
          ))}
        </div>
        {groups.map((g) => (
          <Fragment key={g.name}>
            <div className="border-b border-line bg-paper px-3 py-1 text-[12px] font-medium text-steel">
              <h3>{g.name}</h3>
            </div>
            {g.people.map((e) => (
              <div key={e.id} className="grid border-b border-line last:border-b-0" style={gridStyle}>
                <div
                  className="sticky left-0 z-10 row-start-1 min-w-0 bg-surface px-3 py-1.5"
                  style={{ gridColumn: 1 }}
                >
                  <p className="truncate text-[13px]">{e.fullName}</p>
                  {e.positionName && <p className="truncate text-[11.5px] text-steel">{e.positionName}</p>}
                </div>
                {days.map((d, i) => (
                  <div
                    key={d.iso}
                    aria-hidden
                    className={clsx(
                      'row-start-1 border-l border-line/60',
                      d.weekend && 'bg-sunken/50',
                      d.iso === today && 'bg-signal-soft/60',
                    )}
                    style={{ gridColumn: i + 2 }}
                  />
                ))}
                {e.absences.map((a) => {
                  const color = ABSENCE_COLORS[a.type];
                  const label = `${ABSENCE_TYPE_LABELS[a.type]}${a.pending ? ' (на согласовании)' : ''}: ${formatRange(a.startDate, a.endDate)}${a.comment && !a.pending ? `, ${a.comment}` : ''}`;
                  return (
                    <button
                      key={a.id + (a.pending ? 'p' : '')}
                      type="button"
                      title={label}
                      aria-label={`${e.fullName}. ${label}`}
                      onClick={() => onSelect(a, e.fullName)}
                      className={clsx(
                        'relative z-[5] row-start-1 mx-px my-2 min-w-0 truncate rounded px-1 text-left text-[11px] font-medium leading-[20px]',
                        a.pending ? 'border border-dashed' : 'text-white',
                      )}
                      style={{
                        gridColumn: `${col(later(a.startDate, bounds.from))} / ${col(earlier(a.endDate, bounds.to)) + 1}`,
                        background: a.pending ? `${color}22` : color,
                        borderColor: a.pending ? color : undefined,
                        color: a.pending ? color : undefined,
                      }}
                    >
                      {ABSENCE_TYPE_LABELS[a.type]}
                      {wide && a.comment && !a.pending ? ` · ${a.comment}` : ''}
                      {wide && a.pending ? ' · на согласовании' : ''}
                    </button>
                  );
                })}
              </div>
            ))}
          </Fragment>
        ))}
      </section>
    </div>
  );
}

/** Масштаб «День»: кто отсутствует, по типам, с периодом и комментарием. */
function DayList({
  chart,
  day,
  onSelect,
}: {
  chart: AbsenceChart;
  day: string;
  onSelect: (item: AbsenceItem, name: string) => void;
}) {
  const rows = chart.employees.flatMap((e) =>
    e.absences.filter((a) => a.startDate <= day && a.endDate >= day).map((a) => ({ e, a })),
  );
  const byType = ABSENCE_TYPES.map((t) => ({ t, rows: rows.filter((r) => r.a.type === t) })).filter(
    (g) => g.rows.length,
  );
  const absent = new Set(rows.filter((r) => !r.a.pending).map((r) => r.e.id)).size;
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13.5px] text-ink-soft">
        Отсутствуют: <b className="tabular-nums text-ink">{absent}</b> из {chart.employees.length}
        {rows.some((r) => r.a.pending) && ' (плюс заявления на согласовании)'}
      </p>
      {byType.length === 0 ? (
        <p className="panel px-4 py-8 text-center text-steel">В этот день все на месте.</p>
      ) : (
        byType.map((g) => (
          <section key={g.t} className="panel" aria-label={ABSENCE_TYPE_LABELS[g.t]}>
            <h3 className="flex items-center gap-2 border-b border-line px-4 py-2 text-[13.5px] font-semibold">
              <span aria-hidden className="size-2.5 rounded-sm" style={{ background: ABSENCE_COLORS[g.t] }} />
              {ABSENCE_TYPE_LABELS[g.t]}
              <span className="font-normal tabular-nums text-steel">{g.rows.length}</span>
            </h3>
            <ul className="divide-y divide-line">
              {g.rows.map(({ e, a }) => (
                <li key={a.id + (a.pending ? 'p' : '')}>
                  <button
                    type="button"
                    onClick={() => onSelect(a, e.fullName)}
                    className="flex w-full flex-wrap items-center gap-x-4 gap-y-0.5 px-4 py-2 text-left hover:bg-paper/70"
                  >
                    <span className="flex min-w-0 basis-[240px] items-center gap-2">
                      <Avatar name={e.fullName} employeeId={e.id} size={26} />
                      <span className="min-w-0">
                        <span className="block truncate text-[13.5px] font-medium">{e.fullName}</span>
                        <span className="block truncate text-[12px] text-steel">
                          {[e.positionName, e.departmentName].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                    </span>
                    <span className="text-[13px] tabular-nums text-ink-soft">
                      {formatRange(a.startDate, a.endDate)}
                    </span>
                    {a.pending && <Badge tone="warn">на согласовании</Badge>}
                    {a.comment && !a.pending && <span className="text-[13px] text-steel">{a.comment}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

function AbsenceDetailsDialog({
  item,
  name,
  canManage,
  onClose,
}: {
  item: AbsenceItem;
  name: string;
  canManage: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api(`/absences/${item.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['absences'] });
      onClose();
    },
  });
  const link =
    item.source?.entityType === 'vacation'
      ? { to: '/vacations/$id' as const, label: 'Открыть заявление' }
      : item.source?.entityType === 'request'
        ? { to: '/requests/$id' as const, label: 'Открыть заявку' }
        : null;
  return (
    <Dialog title={ABSENCE_TYPE_LABELS[item.type]} onClose={onClose} width={440}>
      <div className="flex flex-col gap-1 px-5 py-4">
        <p className="font-medium">{name}</p>
        <p>{formatRange(item.startDate, item.endDate)}</p>
        {item.comment && <p className="text-steel">{item.comment}</p>}
        {!item.source && <p className="text-[13px] text-steel">Внесено вручную</p>}
        {remove.error && <p className="text-[13px] text-danger">{remove.error.message}</p>}
      </div>
      <div className="flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3">
        {!item.source && canManage && (
          <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
            Удалить
          </Button>
        )}
        {link && item.source && (
          <Link
            to={link.to}
            params={{ id: item.source.entityId }}
            className="inline-flex h-9 items-center px-3 link"
          >
            {link.label}
          </Link>
        )}
        <Button variant="secondary" onClick={onClose}>
          Закрыть
        </Button>
      </div>
    </Dialog>
  );
}

function AbsenceDialog({ chart, onClose }: { chart: AbsenceChart; onClose: () => void }) {
  const qc = useQueryClient();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api('/absences', { method: 'POST', body }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['absences'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setErrors(err.fields);
        setFormError(Object.keys(err.fields).length ? null : err.message);
      }
    },
  });
  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setErrors({});
    save.mutate(Object.fromEntries(fd.entries()));
  }
  return (
    <Dialog title="Внести отсутствие" onClose={onClose} width={520}>
      <form onSubmit={onSubmit}>
        <div className="flex flex-col gap-4 px-5 py-4">
          <Field label="Сотрудник" error={errors.employeeId}>
            {(p) => (
              <Select {...p} name="employeeId" required defaultValue="">
                <option value="" disabled>
                  Выберите сотрудника
                </option>
                {chart.employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.fullName}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Причина" error={errors.type}>
            {(p) => (
              <Select {...p} name="type" defaultValue="sick">
                {ABSENCE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {ABSENCE_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="С" error={errors.startDate}>
              {(p) => <Input {...p} type="date" name="startDate" required />}
            </Field>
            <Field label="По" error={errors.endDate}>
              {(p) => <Input {...p} type="date" name="endDate" required />}
            </Field>
          </div>
          <Field label="Комментарий" error={errors.comment} hint="Например, номер больничного листа">
            {(p) => <Input {...p} name="comment" maxLength={500} />}
          </Field>
          {formError && <p className="text-[13px] text-danger">{formError}</p>}
        </div>
        <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" loading={save.isPending}>
            Сохранить
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------- Заявления ----------

function VacationList({
  scope,
  search,
  setSearch,
  onCreate,
}: {
  scope: 'mine' | 'all';
  search: VacationsSearch;
  setSearch: (p: Partial<VacationsSearch>) => void;
  onCreate: () => void;
}) {
  const people = useQuery({ ...assigneesQuery, enabled: scope === 'all' });
  const filtered = Boolean(search.person || search.from || search.to || search.kind);
  const q = useQuery(
    vacationsQuery({
      scope,
      userId: scope === 'all' ? search.person : undefined,
      from: search.from,
      to: search.to,
      type: search.kind,
    }),
  );
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end gap-3">
        {scope === 'all' && (
          <Select
            aria-label="Сотрудник"
            value={search.person ?? ''}
            onChange={(e) => setSearch({ person: e.target.value || undefined })}
            className="w-full sm:w-[230px]"
          >
            <option value="">Все сотрудники</option>
            {people.data?.map((p) => (
              <option key={p.userId} value={p.userId}>
                {p.fullName}
              </option>
            ))}
          </Select>
        )}
        <Select
          aria-label="Вид отпуска"
          value={search.kind ?? ''}
          onChange={(e) => setSearch({ kind: (e.target.value || undefined) as VacationType | undefined })}
          className="w-full sm:w-[200px]"
        >
          <option value="">Все виды</option>
          {VACATION_TYPES.map((t) => (
            <option key={t} value={t}>
              {VACATION_TYPE_LABELS[t]}
            </option>
          ))}
        </Select>
        <div className="w-full sm:w-[300px]">
          <DateRange
            label="Отпуск в период"
            from={search.from}
            to={search.to}
            onChange={(from, to) => setSearch({ from, to })}
          />
        </div>
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorBox error={q.error} />
      ) : !q.data.items.length ? (
        <div className="panel px-6 py-10 text-center">
          <p className="font-medium">
            {filtered ? 'По этим условиям заявлений нет.' : 'Заявлений пока нет.'}
          </p>
          {scope === 'mine' && !filtered && (
            <button type="button" className="link mt-2 text-[13.5px]" onClick={onCreate}>
              Подать заявление на отпуск
            </button>
          )}
        </div>
      ) : (
        <VacationTable items={q.data.items} showEmployee={scope === 'all'} />
      )}
    </div>
  );
}

export function VacationTable({ items, showEmployee }: { items: VacationListItem[]; showEmployee: boolean }) {
  return (
    <div className="panel overflow-x-auto">
      <table className="w-full min-w-[720px] text-left">
        <thead>
          <tr className="border-b border-line text-[13px] text-steel">
            <th scope="col" className="w-14 px-4 py-2.5 font-medium">
              №
            </th>
            {showEmployee && (
              <th scope="col" className="px-4 py-2.5 font-medium">
                Сотрудник
              </th>
            )}
            <th scope="col" className="px-4 py-2.5 font-medium">
              Период
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Статус
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Подано
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {items.map((v) => (
            <tr key={v.id} className="hover:bg-paper/70">
              <td className="px-4 py-2.5 tabular-nums text-steel">{v.number}</td>
              {showEmployee && (
                <td className="px-4 py-2.5">
                  <p>{v.employee.fullName}</p>
                  <p className="text-[12.5px] text-steel">{v.employee.departmentName}</p>
                </td>
              )}
              <td className="px-4 py-2.5">
                <Link to="/vacations/$id" params={{ id: v.id }} className="font-medium hover:text-link">
                  {formatRange(v.startDate, v.endDate)}
                </Link>
                <p className="text-[12.5px] text-steel">
                  {v.days} {plural(v.days, ['день', 'дня', 'дней'])}
                  {v.type !== 'annual' && `, ${VACATION_TYPE_LABELS[v.type].toLowerCase()}`}
                </p>
              </td>
              <td className="px-4 py-2.5">
                <EntityStatus status={v.status} label={VACATION_STATUS_LABELS[v.status]} />
                <div>
                  <ProcessHint summary={v.process} />
                </div>
              </td>
              <td className="px-4 py-2.5 text-[13px] text-steel">{formatMoment(v.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VacationFormDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const today = todayIso();
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const days =
    start && end && end >= start ? Math.round((Date.parse(end) - Date.parse(start)) / 86_400_000) + 1 : null;

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ id: string }>('/vacations', { method: 'POST', body }),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['vacations'] });
      await qc.invalidateQueries({ queryKey: ['absences'] });
      onClose();
      await navigate({ to: '/vacations/$id', params: { id: res.id } });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setErrors(err.fields);
        setFormError(err.message);
      }
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const body = {
      type: String(fd.get('type')),
      startDate: start,
      endDate: end,
      comment: String(fd.get('comment') ?? ''),
    };
    const parsed = createVacationSchema.safeParse(body);
    setFormError(null);
    if (!parsed.success) {
      const f: Record<string, string> = {};
      for (const i of parsed.error.issues) f[i.path.join('.')] ??= i.message;
      setErrors(f);
      return;
    }
    setErrors({});
    save.mutate(body);
  }

  return (
    <Dialog title="Заявление на отпуск" onClose={onClose} width={520}>
      <form onSubmit={onSubmit} noValidate>
        <div className="flex flex-col gap-4 px-5 py-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Начало" error={errors.startDate}>
              {(p) => (
                <Input
                  {...p}
                  type="date"
                  min={today}
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  required
                />
              )}
            </Field>
            <Field label="Окончание" error={errors.endDate}>
              {(p) => (
                <Input
                  {...p}
                  type="date"
                  min={start || today}
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  required
                />
              )}
            </Field>
          </div>
          <p className="-mt-2 text-[13px] text-steel" aria-live="polite">
            {days
              ? `${days} ${plural(days, ['календарный день', 'календарных дня', 'календарных дней'])}`
              : ' '}
          </p>
          <Field label="Вид отпуска" error={errors.type}>
            {(p) => (
              <Select {...p} name="type" defaultValue="annual">
                {VACATION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {VACATION_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Комментарий" error={errors.comment}>
            {(p) => <Textarea {...p} name="comment" maxLength={2000} />}
          </Field>
          {formError && !Object.keys(errors).length && (
            <p role="alert" className="text-[13px] text-danger">
              {formError}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" loading={save.isPending}>
            Отправить на согласование
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
